import type { Address } from './valueObjects/address';
import type { ApplyDateTime } from './valueObjects/applyDateTime';
import type { ApplyStatus } from './valueObjects/applyStatus';
import type { Birthday } from './valueObjects/birthday';
import type { ChildFamilyName } from './valueObjects/childFamilyName';
import type { ChildFamilyNameKana } from './valueObjects/childFamilyNameKana';
import type { ChildId } from './valueObjects/childId';
import type { ChildName } from './valueObjects/childName';
import type { ChildNameKana } from './valueObjects/childNameKana';
import type { Email } from './valueObjects/email';
import type { FacilityId } from './valueObjects/facilityId';
import type { FacilityName } from './valueObjects/facilityName';
import type { Gender } from './valueObjects/gender';
import type { ParentFamilyName } from './valueObjects/parentFamilyName';
import type { ParentFamilyNameKana } from './valueObjects/parentFamilyNameKana';
import type { ParentId } from './valueObjects/parentId';
import type { ParentName } from './valueObjects/parentName';
import type { ParentNameKana } from './valueObjects/parentNameKana';
import type { PostalCode } from './valueObjects/postalCode';
import type { Tel } from './valueObjects/tel';
import type { ChildApplyJsonPub, ChildApplyJsonPvt } from '@/types/domain/childApply/entities/childApplyEntity';

import { EntityPub } from '@/domain/entityPub';
import { DeleteType } from '@/domain/entityRepositoryBase';
import {
  EntityName,
} from '@/infra/appsync';

/**
 * 申請プロパティ非公開
 */
export type ChildApplyPropsPvt = {
  /** 子どもID */
  childId: ChildId,
  /** 施設ID */
  facilityId: FacilityId,
  /** 施設名 */
  facilityName: FacilityName,
  /** 子名前 */
  childName: ChildName,
  /** 子苗字 */
  childFamilyName: ChildFamilyName,
  /** 子名前かな */
  childNameKana: ChildNameKana,
  /** 子苗字かな */
  childFamilyNameKana: ChildFamilyNameKana,
  /** 性別 */
  gender: Gender,
  /** 誕生日 */
  birthday: Birthday,
  /** メール */
  email: Email,
  /** 親ID */
  parentId: ParentId,
  /** 親名前 */
  parentName: ParentName,
  /** 親苗字 */
  parentFamilyName: ParentFamilyName,
  /** 親名前かな */
  parentNameKana: ParentNameKana,
  /** 親苗字かな */
  parentFamilyNameKana: ParentFamilyNameKana,
  /** 郵便番号 */
  postalCode: PostalCode,
  /** 住所 */
  address: Address,
  /** 電話番号 */
  tel: Tel,
  /** 申請日時 */
  applyDateTime: ApplyDateTime,
}

/**
 * 申請プロパティ公開
 */
export type ChildApplyPropsPub = {
  /** 申請ステータス */
  applyStatus: ApplyStatus,
}

/**
 * 申請Entity
 */
export class ChildApplyEntity extends EntityPub<
  ChildApplyPropsPvt,
  ChildApplyJsonPvt,
  ChildApplyPropsPub,
  ChildApplyJsonPub
> {
  get deleteType(): DeleteType {
    return DeleteType.Dt;
  }

  get deleteKey(): string {
    return this.getFutureMonth(this.applyDateTime.value, 1).toJSON();
  }

  toJsonData(): ChildApplyJsonPvt {
    return {
      childId: this.childId.value,
      facilityId: this.facilityId.value,
      facilityName: this.facilityName.value,
      childName: this.childName.value,
      childFamilyName: this.childFamilyName.value,
      childNameKana: this.childNameKana.value,
      childFamilyNameKana: this.childFamilyNameKana.value,
      gender: this.gender.value,
      birthday: this.birthday.value.toJSON(),
      email: this.email.value,
      parentId: this.parentId.value,
      parentName: this.parentName.value,
      parentFamilyName: this.parentFamilyName.value,
      parentNameKana: this.parentNameKana.value,
      parentFamilyNameKana: this.parentFamilyNameKana.value,
      postalCode: this.postalCode.value,
      address: this.address.value,
      tel: this.tel.value,
      applyDateTime: this.applyDateTime.value.toJSON(),
    };
  }

  toJsonPubData(): ChildApplyJsonPub {
    return {
      applyStatus: this.applyStatus.value,
    };
  }

  /** labelApplyInfo */
  static get labelApplyInfo() {
    return '申請内容';
  }

  /** labelChildInfo */
  static get labelChildInfo() {
    return '子ども情報';
  }

  /** labelParentInfo */
  static get labelParentInfo() {
    return '保護者情報';
  }

  /** labelName */
  static get labelName() {
    return '氏名';
  }

  /** labelNameKana */
  static get labelNameKana() {
    return 'Name';
  }

  /**
   * Entity名
   */
  get entityName(): EntityName {
    const name: EntityName = EntityName.WagakoNurseryChildApplyChildApplyEntity;
    return name;
  }

  /**
   * Entityキー
   */
  get entityKey(): string {
    return `${this.applyDateTime.toStringDateTime()}#${this.facilityId.value}#${this.parentId.value}_${this.childId.value}`;
  }

  /** Entityキー子どもID */
  get entityKeyChildId(): string {
    return this.childId.value;
  }

  /** 子どもID */
  get childId(): ChildId {
    return this._value.childId;
  }

  /** 親ID */
  get parentId(): ParentId {
    return this._value.parentId;
  }

  /** 子名前 */
  get childName(): ChildName {
    return this._value.childName;
  }

  /** 子名前 */
  set childName(childName: ChildName) {
    this._value.childName = childName;
  }

  /** 子名前かな */
  get childNameKana(): ChildNameKana {
    return this._value.childNameKana;
  }

  /** 子名前かな */
  set childNameKana(childNameKana: ChildNameKana) {
    this._value.childNameKana = childNameKana;
  }

  /** 子苗字 */
  get childFamilyName(): ChildFamilyName {
    return this._value.childFamilyName;
  }

  /** 子苗字 */
  set childFamilyName(childFamilyName: ChildFamilyName) {
    this._value.childFamilyName = childFamilyName;
  }

  /** 子苗字かな */
  get childFamilyNameKana(): ChildNameKana {
    return this._value.childFamilyNameKana;
  }

  /** 子苗字かな */
  set childFamilyNameKana(childFamilyNameKana: ChildNameKana) {
    this._value.childFamilyNameKana = childFamilyNameKana;
  }

  /** 性別 */
  get gender(): Gender {
    return this._value.gender;
  }

  /** 性別 */
  set gender(gender: Gender) {
    this._value.gender = gender;
  }

  /** 誕生日 */
  get birthday(): Birthday {
    return this._value.birthday;
  }

  /** 誕生日 */
  set birthday(birthday: Birthday) {
    this._value.birthday = birthday;
  }

  /** 施設ID */
  get facilityId(): FacilityId {
    return this._value.facilityId;
  }

  /** 施設ID */
  set facilityId(facilityId: FacilityId) {
    this._value.facilityId = facilityId;
  }

  /** 施設名 */
  get facilityName(): FacilityName {
    return this._value.facilityName;
  }

  /** 施設名 */
  set facilityName(facilityName: FacilityName) {
    this._value.facilityName = facilityName;
  }

  /** 親名前 */
  get parentName(): ParentName {
    return this._value.parentName;
  }

  /** 親名前 */
  set parentName(parentName: ParentName) {
    this._value.parentName = parentName;
  }

  /** 親名前かな */
  get parentNameKana(): ParentNameKana {
    return this._value.parentNameKana;
  }

  /** 親名前かな */
  set parentNameKana(parentNameKana: ParentNameKana) {
    this._value.parentNameKana = parentNameKana;
  }

  /** 親苗字 */
  get parentFamilyName(): ParentName {
    return this._value.parentFamilyName;
  }

  /** 親苗字 */
  set parentFamilyName(parentFamilyName: ParentName) {
    this._value.parentFamilyName = parentFamilyName;
  }

  /** 親苗字かな */
  get parentFamilyNameKana(): ParentNameKana {
    return this._value.parentFamilyNameKana;
  }

  /** 親苗字かな */
  set parentFamilyNameKana(parentFamilyNameKana: ParentNameKana) {
    this._value.parentFamilyNameKana = parentFamilyNameKana;
  }

  /** メール */
  get email(): Email {
    return this._value.email;
  }

  /** メール */
  set email(email: Email) {
    this._value.email = email;
  }

  /** 郵便番号 */
  get postalCode(): PostalCode {
    return this._value.postalCode;
  }

  /** 郵便番号 */
  set postalCode(postalCode: PostalCode) {
    this._value.postalCode = postalCode;
  }

  /** 住所 */
  get address(): Address {
    return this._value.address;
  }

  /** 住所 */
  set address(address: Address) {
    this._value.address = address;
  }

  /** 電話番号 */
  get tel(): Tel {
    return this._value.tel;
  }

  /** 電話番号 */
  set tel(tel: Tel) {
    this._value.tel = tel;
  }

  /** 申請日時 */
  get applyDateTime(): ApplyDateTime {
    return this._value.applyDateTime;
  }

  /** 申請日時 */
  set applyDateTime(applyDateTime: ApplyDateTime) {
    this._value.applyDateTime = applyDateTime;
  }

  /** 申請ステータス */
  get applyStatus(): ApplyStatus {
    return this._valuePubData.data.applyStatus;
  }

  /** 申請ステータス */
  set applyStatus(applyStatus: ApplyStatus) {
    this._valuePubData.data.applyStatus = applyStatus;
  }
}
